/**
 * The startup sequence's one guarantee: SOMETHING is rendered.
 *
 * index.js's `bootstrap()` awaits `initPreferencesFromDisk()` and only then
 * calls `root.render`. It had no `.catch()`, so a rejection there — a corrupt
 * preferences file, an fs permission the Tauri build did not grant, a disk
 * error — skipped `root.render` entirely and left an EMPTY `#root`: a white
 * window, plus an unhandled promise rejection in a console nobody has open.
 *
 * An error boundary cannot cover this. Boundaries catch errors thrown while
 * React renders a tree; here the failure happens before any tree exists.
 * The only fix is a catch that renders the fallback itself.
 *
 * This lives in its own module rather than inline in index.js so it can be
 * tested against a real React root without importing App (and with it monaco,
 * i18n, lexical and the whole component graph) into the test process.
 */

/**
 * @param {object} opts
 * @param {() => (Promise<void>|void)} opts.boot        async startup work
 * @param {() => void} opts.renderApp                    renders the real tree
 * @param {(error: unknown) => void} opts.renderFallback renders the crash screen
 * @returns {Promise<void>} resolves once one of the two renders has run
 */
export function startApp({ boot, renderApp, renderFallback }) {
  return Promise.resolve()
    .then(() => boot())
    .then(() => renderApp())
    .catch((error) => {
      console.error('[app] startup failed:', error);
      try {
        renderFallback(error);
      } catch (fallbackError) {
        // Even the fallback failed. Leave a trace rather than a silent white
        // page — there is nothing left to render into.
        console.error('[app] startup fallback failed to render:', fallbackError);
      }
    });
}

/** Best-effort readable detail for the crash screen. Tauri rejects with strings. */
export function startupErrorDetail(error) {
  if (typeof error === 'string') return error;
  if (typeof error?.stack === 'string' && error.stack) return error.stack;
  return String(error?.message || error);
}
